/**
 * P1 QC Workflow - My Workspace
 * 
 * Personal QC workspace showing orders assigned to the current user.
 * KPIs: My Assigned, My Completed Today, My Rejections Today, My Avg Review Time, My SLA Breaches
 * Table filtered by qc_assigned_to = current_user.id
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  ArrowUpRight,
  Timer,
  AlertOctagon,
  ArrowLeft,
  Briefcase,
  UserMinus
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../App';
import QCApproveModal from '../components/qc/QCApproveModal';
import QCEscalateModal from '../components/qc/QCEscalateModal';
import { formatDateTimeLocal } from '../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

/**
 * QC KPI Card Component
 */
const QCKPICard = ({ icon: Icon, label, value, subValue, colorClass = 'text-blue-600', bgClass = 'bg-blue-50' }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${colorClass}`}>{value}</p>
        {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
      </div>
      <div className={`p-2 rounded-lg ${bgClass}`}>
        <Icon className={`w-5 h-5 ${colorClass}`} />
      </div>
    </div>
  </div>
);

/**
 * SLA Badge Component
 */
const SLABadge = ({ submittedAt, slaThresholdHours }) => {
  const [elapsed, setElapsed] = useState({ text: '', hours: 0 });

  useEffect(() => {
    const calculateElapsed = () => {
      if (!submittedAt) {
        setElapsed({ text: 'N/A', hours: 0 });
        return;
      }
      const submitted = new Date(submittedAt);
      const now = new Date();
      const diffMs = now - submitted;
      const diffHours = diffMs / (1000 * 60 * 60);
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      setElapsed({ text: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`, hours: diffHours });
    };
    calculateElapsed();
    const interval = setInterval(calculateElapsed, 60000);
    return () => clearInterval(interval);
  }, [submittedAt]);

  let badgeClass = 'bg-gray-100 text-gray-700';
  let showIcon = false;
  let pulseClass = '';
  
  if (elapsed.hours >= 8) {
    badgeClass = 'bg-red-100 text-red-800 border border-red-300';
    showIcon = true;
    pulseClass = 'animate-pulse';
  } else if (elapsed.hours >= slaThresholdHours) {
    badgeClass = 'bg-red-100 text-red-700 border border-red-200';
    showIcon = true;
  } else if (elapsed.hours >= 2) {
    badgeClass = 'bg-amber-100 text-amber-700 border border-amber-200';
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-sm font-medium rounded-2xl ${badgeClass} ${pulseClass}`}>
      {elapsed.text}
      {showIcon && <AlertTriangle className="w-3 h-3" />}
    </span>
  );
};

/**
 * QC Workspace Main Component
 */
const QCWorkspace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Config & Data state
  const [config, setConfig] = useState({ sla_threshold_hours: 4 });
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  
  // Modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  // QC-CONSOLIDATE-001 (Feb 2026): showRejectModal removed.
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState({});

  // Fetch config once
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/qc/config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
        }
      } catch (err) {
        console.error('Error fetching config:', err);
      }
    };
    fetchConfig();
  }, []);

  // Fetch my workspace stats
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/qc/my-workspace/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  // Fetch my assigned orders
  const fetchOrders = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/qc/orders?tab=qc_review&assigned_to_me=true&sort_by=time_in_qc&sort_order=asc`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
        setLastRefreshed(new Date());
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchStats();
    fetchOrders();
  }, [fetchStats, fetchOrders]);

  // Handle unassign
  const handleUnassign = async (order) => {
    setIsUnassigning(prev => ({ ...prev, [order.id]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/orders/${order.id}/qc-unassign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`Order ${order.order_number} unassigned`);
        fetchStats();
        fetchOrders(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to unassign order');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsUnassigning(prev => ({ ...prev, [order.id]: false }));
    }
  };

  // Handle approve
  const handleApproveClick = (order) => {
    if (order.qc_submitted_by === user?.id) {
      toast.error('Separation of duties: You cannot approve orders you submitted.');
      return;
    }
    setSelectedOrder(order);
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async (notes, extra = {}) => {
    if (!selectedOrder) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/orders/${selectedOrder.id}/qc-approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || null, ...extra })
      });
      if (response.ok) {
        toast.success(`Order ${selectedOrder.order_number} approved!`);
        setTimeout(() => { setShowApproveModal(false); setSelectedOrder(null); fetchStats(); fetchOrders(true); }, 900);
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail?.message || errorData.detail || 'Failed to approve order', { duration: errorData.detail?.error === 'TAX_NARRATIVE_MISSING' ? 12000 : 4000 });
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    }
    return false;
  };

  // QC-CONSOLIDATE-001 (Feb 2026): Reject handlers removed.
  // Critical issues now flow through Return-for-Correction with
  // severity='critical' from the Quality Command Center.

  // Handle escalate
  const handleEscalateClick = (order) => {
    setSelectedOrder(order);
    setShowEscalateModal(true);
  };

  const handleEscalateConfirm = async (reason) => {
    if (!selectedOrder || !reason) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/orders/${selectedOrder.id}/qc-escalate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ escalation_reason: reason })
      });
      if (response.ok) {
        toast.success(`Order ${selectedOrder.order_number} escalated.`);
        setShowEscalateModal(false);
        setSelectedOrder(null);
        fetchStats();
        fetchOrders(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to escalate order');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    }
  };

  // Navigate to order
  const handleViewOrder = (order) => {
    navigate(`/orders/${order.id}/fulfillment?qc_view=true`);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTimeLocal(dateString);
  };

  // Format last refreshed
  const formatLastRefreshed = () => {
    if (!lastRefreshed) return '';
    // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local refresh clock
    return lastRefreshed.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const priorityMap = {
      'rush': { label: 'Rush', class: 'bg-red-100 text-red-800 border-red-200' },
      'super_rush': { label: 'Super Rush', class: 'bg-red-200 text-red-900 border-red-300' },
      'normal': { label: 'Normal', class: 'bg-gray-100 text-gray-600 border-gray-200' },
      'low': { label: 'Low', class: 'bg-blue-100 text-blue-600 border-blue-200' }
    };
    const p = priorityMap[priority?.toLowerCase()] || priorityMap['normal'];
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-2xl border ${p.class}`}>{p.label}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="qc-workspace">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-indigo-900 border-b border-indigo-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/qc-dashboard')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="border-l border-indigo-600 h-8" />
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Briefcase className="w-6 h-6" />
                My Workspace
              </h1>
              <p className="text-sm text-indigo-200 mt-1">Personal QC assignments for {user?.full_name || 'User'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastRefreshed && (
              <span className="text-xs text-indigo-300">Last refreshed: {formatLastRefreshed()}</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchStats(); fetchOrders(true); }}
              disabled={isRefreshing}
              className="flex items-center gap-2 bg-white/10 border-indigo-400 text-white hover:bg-white/20"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Personal KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <QCKPICard icon={Briefcase} label="My Assigned" value={stats?.my_assigned || 0} colorClass="text-indigo-600" bgClass="bg-indigo-50" />
          <QCKPICard icon={CheckCircle} label="My Completed Today" value={stats?.my_completed_today || 0} colorClass="text-green-600" bgClass="bg-green-50" />
          <QCKPICard icon={XCircle} label="My Rejections Today" value={stats?.my_rejections_today || 0} colorClass="text-red-600" bgClass="bg-red-50" />
          <QCKPICard icon={Timer} label="My Avg Review Time" value={stats?.my_avg_review_time || 'N/A'} subValue="hours" colorClass="text-blue-600" bgClass="bg-blue-50" />
          <QCKPICard 
            icon={AlertOctagon} 
            label="My SLA Breaches" 
            value={stats?.my_sla_breaches || 0} 
            subValue={`>${config.sla_threshold_hours}h threshold`}
            colorClass={stats?.my_sla_breaches > 0 ? "text-red-600" : "text-gray-600"}
            bgClass={stats?.my_sla_breaches > 0 ? "bg-red-50" : "bg-gray-50"}
          />
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">My Assigned Orders</h2>
            <p className="text-sm text-gray-500">Orders you have claimed for QC review</p>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">Error loading orders</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">Loading your orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No assigned orders</h2>
              <p className="text-gray-500 mb-4">You haven't claimed any orders for review yet.</p>
              <Button onClick={() => navigate('/qc-dashboard')} variant="outline">
                Go to QC Dashboard to assign orders
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="tf-thead">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Waiting</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => {
                    const isSameUser = order.qc_submitted_by === user?.id;
                    
                    return (
                      <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-900">{order.order_number}</span>
                          {order.typing_status === 'returned_to_qc' && (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide align-middle"
                              title={order.typing_return_reason || 'Returned from Typing'} data-testid={`qc-ws-returned-from-typing-${order.id}`}>Returned from Typing</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700">{order.qc_submitted_by_name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <SLABadge submittedAt={order.qc_submitted_at} slaThresholdHours={config.sla_threshold_hours} />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">{getPriorityBadge(order.priority)}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleViewOrder(order)} className="flex items-center gap-1">
                              View <ChevronRight className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnassign(order)}
                              disabled={isUnassigning[order.id]}
                              className="flex items-center gap-1 text-gray-600 hover:text-gray-700 hover:bg-gray-50 border-gray-200"
                            >
                              <UserMinus className="w-4 h-4" />
                              {isUnassigning[order.id] ? '...' : 'Unassign'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEscalateClick(order)}
                              className="flex items-center gap-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                            >
                              <ArrowUpRight className="w-4 h-4" />
                              Escalate
                            </Button>
                            {/* QC-CONSOLIDATE-001 (Feb 2026): Reject button removed.
                                Use the Quality Command Center's Return-for-Correction flow
                                with severity='critical' for hard issues. */}
                            <Button
                              size="sm"
                              onClick={() => handleApproveClick(order)}
                              disabled={isSameUser}
                              className={`flex items-center gap-1 ${isSameUser ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white`}
                              title={isSameUser ? 'Cannot approve your own submission' : 'Approve'}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <QCApproveModal isOpen={showApproveModal} onClose={() => { setShowApproveModal(false); setSelectedOrder(null); }} onConfirm={handleApproveConfirm} order={selectedOrder} />
      <QCEscalateModal isOpen={showEscalateModal} onClose={() => { setShowEscalateModal(false); setSelectedOrder(null); }} onConfirm={handleEscalateConfirm} order={selectedOrder} />
    </div>
  );
};

export default QCWorkspace;
